import { useState, useEffect, useCallback } from 'react';
import en from '../locales/en.json';
import zhHant from '../locales/zh-Hant.json';
import zhHans from '../locales/zh-Hans.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';

// Define types
type Language = 'en' | 'zh-Hant' | 'zh-Hans' | 'es' | 'fr';
type Translations = typeof en | typeof zhHant | typeof zhHans | typeof es | typeof fr;
// TranslationKey is used for type-safe access to translation keys
type TranslationKey = keyof Translations | string;

// Create a type-safe translation function
export const useLanguage = () => {
  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState<Translations>(en);

  useEffect(() => {
    // Load saved language preference
    const savedLanguage = localStorage.getItem('language') as Language | null;
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    // Save language preference
    localStorage.setItem('language', language);
    
    // Load translations based on selected language
    if (language === 'zh-Hant') {
      setTranslations(zhHant);
    } else if (language === 'zh-Hans') {
      setTranslations(zhHans);
    } else if (language === 'es') {
      setTranslations(es);
    } else if (language === 'fr') {
      setTranslations(fr);
    } else {
      setTranslations(en);
    }
  }, [language]);

  const t = useCallback((key: TranslationKey): any => {
    const keys = key.split('.');
    let result: any = translations;
    for (const k of keys) {
      result = result[k];
      if (result === undefined) {
        return key;
      }
    }
    return result;
  }, [translations]);

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
  };

  return { 
    language, 
    translations, 
    t, 
    changeLanguage 
  };
};
